import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@ibsys/kit';
import { TriangleAlertIcon } from 'lucide-react';
import type { StrategistHierarchyCluster } from '@/entities/strategist';
import { cn } from '@/shared/lib';
import { formatLiftPp, liftColorClass } from '@/shared/lib/utils';
import { formatTargetRate } from '@/shared/lib/utils';
import { RELIABLE_MIN_DIALOGS } from '@/widgets/StrategistHeatmap';

type Props = {
  /** Кластеры текущего среза */
  clusters: StrategistHierarchyCluster[];
  /** Провалиться в диалоги строки (кластера) */
  onRowOpen: (cluster: StrategistHierarchyCluster) => void;
  /** Открыть карточку понятия медоида кластера */
  onConceptOpen: (medoidConceptId: string) => void;
};

// Сколько примеров формулировок показывать в строке.
const EXAMPLES_LIMIT = 3;

/**
 * Таблица кластеров понятий текущего среза: понятие (медоид), число понятий,
 * число диалогов, доля целевого исхода (вероятность выхода на успех) с влиянием
 * относительно базовой доли и примеры формулировок. Отсортирована по числу
 * диалогов (desc). Кластеры с малым числом диалогов (n < {@link RELIABLE_MIN_DIALOGS})
 * приглушаются и помечаются — как ненадёжные ячейки матрицы.
 *
 * Клик по строке проваливает в её диалоги (срез всего кластера — те же n и доля
 * цели, что в строке), клик по названию понятия открывает карточку медоида —
 * тот же расклад, что в рейтинге переходов.
 *
 * Заголовок — на токене `--table-header`, строки подсвечиваются `--table-hover`.
 * Широкая таблица скроллится по горизонтали.
 */
export const HierarchyTable: FC<Props> = ({ clusters, onRowOpen, onConceptOpen }) => {
  const { t } = useTranslation('strategist');

  const sorted = useMemo(() => [...clusters].sort((a, b) => b.nDialogs - a.nDialogs), [clusters]);

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-200 border-collapse text-sm">
        <thead>
          <tr className="bg-table-header text-left">
            <th className="px-3 py-2 font-medium text-muted-foreground">{t('hierarchy.table.concept')}</th>
            <th className="px-3 py-2 font-medium text-muted-foreground text-right whitespace-nowrap">
              {t('hierarchy.table.concepts')}
            </th>
            <th className="px-3 py-2 font-medium text-muted-foreground text-right whitespace-nowrap">
              {t('hierarchy.table.dialogs')}
            </th>
            <th className="px-3 py-2 font-medium text-muted-foreground text-right whitespace-nowrap">
              {t('hierarchy.table.targetRate')}
            </th>
            <th className="px-3 py-2 font-medium text-muted-foreground text-right whitespace-nowrap">
              {t('hierarchy.table.lift')}
            </th>
            <th className="px-3 py-2 font-medium text-muted-foreground">{t('hierarchy.table.examples')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((cluster) => {
            const unreliable = cluster.nDialogs < RELIABLE_MIN_DIALOGS;
            return (
              <tr
                key={cluster.clusterKey}
                role="button"
                tabIndex={-1}
                onClick={() => onRowOpen(cluster)}
                className={cn(
                  'cursor-pointer border-t border-border align-top hover:bg-table-hover',
                  unreliable && 'opacity-60',
                )}
              >
                <td className="px-3 py-2 font-medium text-foreground">
                  <span
                    role="button"
                    tabIndex={-1}
                    className="cursor-pointer hover:text-primary"
                    onClick={(e) => {
                      // Клик по понятию открывает его карточку, не проваливаясь в диалоги строки.
                      e.stopPropagation();
                      onConceptOpen(cluster.medoidConceptId);
                    }}
                  >
                    {cluster.label}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-foreground tabular-nums">
                  {cluster.nConcepts.toLocaleString('ru-RU')}
                </td>
                <td className="px-3 py-2 text-right text-foreground tabular-nums">
                  <span className="inline-flex items-center justify-end gap-1">
                    {unreliable && (
                      <Tooltip tooltip={t('hierarchy.table.unreliableHint')}>
                        <TriangleAlertIcon className="size-3.5 text-muted-foreground" />
                      </Tooltip>
                    )}
                    {cluster.nDialogs.toLocaleString('ru-RU')}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-foreground tabular-nums whitespace-nowrap">
                  {formatTargetRate(cluster.targetRate)}
                </td>
                <td
                  className={cn(
                    'px-3 py-2 text-right font-medium tabular-nums whitespace-nowrap',
                    liftColorClass(cluster.lift),
                  )}
                >
                  {formatLiftPp(cluster.lift)}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  <span className="line-clamp-2">{cluster.examples.slice(0, EXAMPLES_LIMIT).join(' · ')}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
